// src/hooks/useJanetSessions.js — Janet chat TOPICS (janet_chat_sessions).
//
// One row per conversation, keyed by the same client-minted id that
// janet_chat_messages.session_id already carries. Lists / creates / renames /
// pins / archives topics for the signed-in user, and adopts "orphan" sessions
// (turns saved before the sessions table existed) so history isn't lost.
//
// Degrades cleanly when the migration hasn't been applied yet: `available`
// is false, the list is empty, and every writer is a no-op — the chat itself
// keeps working exactly as before (turns still save to janet_chat_messages).
//
// Cross-instance refresh: every write dispatches 'janet:sessions-changed' on
// window; the FAB dropdown, the /janet/chat page and a docked panel all
// re-list on it. Other windows (desktop Janet window, browser popup) refresh
// on the 'storage' event fired by bumping JANET_SESSIONS_TICK_KEY.

import { useState, useEffect, useCallback, useRef } from 'react'
import supabase from '../lib/supabase'
import { newSessionId, deriveTitle } from '../lib/janetTopics'
export { newSessionId, deriveTitle }

export const JANET_SESSIONS_TICK_KEY = 'janet.chat.sessions.tick'
const CHANGED_EVENT = 'janet:sessions-changed'
const LIST_LIMIT = 200
const BASE_COLS = 'id, title, entity_type, entity_id, entity_name, pinned, archived_at, last_message_at, message_count, created_at'
// Topics Janet started (20261012c): who opened it, unread, and what it came from.
const OPENER_COLS = `${BASE_COLS}, started_by, unread, origin_type, origin_id`
let openerColsMissing = false

export function notifySessionsChanged() {
  try { window.dispatchEvent(new CustomEvent(CHANGED_EVENT)) } catch { /* non-browser */ }
  try { localStorage.setItem(JANET_SESSIONS_TICK_KEY, String(Date.now())) } catch { /* storage unavailable */ }
}

// A missing TABLE (migration never run) vs. a missing COLUMN (migration
// partially applied, or an unrelated schema drift) must stay distinguishable:
// the former hides Janet's topics entirely (`available=false`); the latter
// should fall back to BASE_COLS and keep listing. Both can say "does not
// exist" / "schema cache" in their message text, so a bare substring check
// on either phrase alone is not safe — it must also reject any message that
// names a column (PostgREST's missing-column text always does).
function isMissingTable(error) {
  const msg = String(error?.message || '')
  return error?.code === 'PGRST205' || error?.code === '42P01'
    || /relation .* does not exist/i.test(msg)
    || /could not find the table/i.test(msg)
}

function isMissingOpenerColumn(error) {
  return (error?.code === '42703' || error?.code === 'PGRST204')
    && /\b(started_by|unread|origin_type|origin_id)\b/i.test(error?.message || '')
}

export function useJanetSessions(userId) {
  const [sessions, setSessions] = useState([])
  const [loading, setLoading] = useState(() => !!userId)
  const [available, setAvailable] = useState(true)
  const adoptedRef = useRef(false)

  const load = useCallback(async () => {
    if (!userId) return
    const list = (cols) => supabase
      .from('janet_chat_sessions')
      .select(cols)
      .eq('user_id', userId)
      .is('archived_at', null)
      .order('pinned', { ascending: false })
      .order('last_message_at', { ascending: false, nullsFirst: false })
      .limit(LIST_LIMIT)
    let { data, error } = await list(openerColsMissing ? BASE_COLS : OPENER_COLS)
    // Before migration 20261012c the new columns don't exist — list without them.
    if (!openerColsMissing && isMissingOpenerColumn(error)) {
      openerColsMissing = true
      ;({ data, error } = await list(BASE_COLS))
    }
    setLoading(false)
    if (error) {
      if (isMissingTable(error)) setAvailable(false)
      else console.warn('[janet-sessions] list failed:', error.message)
      return
    }
    setAvailable(true)
    setSessions(data || [])
  }, [userId])

  // Adopt sessions that have turns but no row (pre-migration history). Runs
  // once per mount, under the user's own RLS. Titles come from the first user
  // turn; the stamp from the newest turn.
  const adoptOrphans = useCallback(async () => {
    if (!userId || adoptedRef.current) return
    adoptedRef.current = true
    const { data: turns, error } = await supabase
      .from('janet_chat_messages')
      .select('session_id, role, content, created_at, page_context')
      .eq('user_id', userId)
      .order('created_at', { ascending: true })
      .limit(2000)
    if (error || !turns?.length) return
    const { data: existing, error: e2 } = await supabase
      .from('janet_chat_sessions')
      .select('id')
      .eq('user_id', userId)
    if (e2) return
    const have = new Set((existing || []).map(r => r.id))
    const bySession = new Map()
    for (const t of turns) {
      if (!t.session_id || have.has(t.session_id)) continue
      let s = bySession.get(t.session_id)
      if (!s) {
        s = { id: t.session_id, user_id: userId, title: null, created_at: t.created_at, last_message_at: t.created_at, message_count: 0, entity_type: null, entity_id: null, entity_name: null }
        bySession.set(t.session_id, s)
      }
      s.message_count += 1
      s.last_message_at = t.created_at
      if (!s.title && t.role === 'user') s.title = deriveTitle(t.content)
      const pc = t.page_context
      if (pc?.entityType && !s.entity_type) {
        s.entity_type = String(pc.entityType)
        s.entity_id = pc.entityId ? String(pc.entityId) : null
        s.entity_name = pc.entityName || null
      }
    }
    if (bySession.size === 0) return
    const rows = [...bySession.values()].map(s => ({ ...s, title: s.title || 'Conversation' }))
    const { error: e3 } = await supabase.from('janet_chat_sessions').upsert(rows, { onConflict: 'id', ignoreDuplicates: true })
    if (e3) console.warn('[janet-sessions] adopt failed:', e3.message)
    else await load()
  }, [userId, load])

  useEffect(() => {
    if (!userId) return
    load().then(() => { if (available) adoptOrphans() })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId])

  useEffect(() => {
    const onChanged = () => load()
    const onStorage = (e) => { if (e.key === JANET_SESSIONS_TICK_KEY) load() }
    window.addEventListener(CHANGED_EVENT, onChanged)
    window.addEventListener('storage', onStorage)
    return () => {
      window.removeEventListener(CHANGED_EVENT, onChanged)
      window.removeEventListener('storage', onStorage)
    }
  }, [load])

  const write = useCallback(async (fn) => {
    if (!userId || !available) return false
    const { error } = await fn()
    if (error) {
      if (isMissingTable(error)) setAvailable(false)
      else console.warn('[janet-sessions] write failed:', error.message)
      return false
    }
    notifySessionsChanged()
    return true
  }, [userId, available])

  const rename  = useCallback((id, title) => write(() => supabase.from('janet_chat_sessions').update({ title: deriveTitle(title) || 'Conversation', updated_at: new Date().toISOString() }).eq('id', id).eq('user_id', userId)), [write, userId])
  const setPinned = useCallback((id, pinned) => write(() => supabase.from('janet_chat_sessions').update({ pinned: !!pinned, updated_at: new Date().toISOString() }).eq('id', id).eq('user_id', userId)), [write, userId])
  const archive = useCallback((id) => write(() => supabase.from('janet_chat_sessions').update({ archived_at: new Date().toISOString(), updated_at: new Date().toISOString() }).eq('id', id).eq('user_id', userId)), [write, userId])
  // Delete removes the topic AND its turns (the turns table has no FK to cascade).
  // Opening a topic Janet started marks it read. No-op before 20261012c.
  const markRead = useCallback(async (id) => {
    if (!userId || openerColsMissing) return false
    const row = sessions.find(s => s.id === id)
    if (!row?.unread) return false
    setSessions(prev => prev.map(s => (s.id === id ? { ...s, unread: false } : s)))
    return write(() => supabase.from('janet_chat_sessions').update({ unread: false }).eq('id', id).eq('user_id', userId))
  }, [write, userId, sessions])
  const unreadCount = sessions.filter(s => s.unread).length
  const remove  = useCallback(async (id) => {
    if (!userId) return false
    await supabase.from('janet_chat_messages').delete().eq('user_id', userId).eq('session_id', id)
    return write(() => supabase.from('janet_chat_sessions').delete().eq('id', id).eq('user_id', userId))
  }, [write, userId])

  return { sessions, loading, available, reload: load, rename, setPinned, archive, remove, markRead, unreadCount }
}

// Ensure a session row exists / is current. Called by the memory hook on each
// saved turn — cheap upsert; the title is set once from the first user turn.
export async function touchSession({ userId, sessionId, role, content, page, increment = 1 }) {
  if (!userId || !sessionId) return
  const now = new Date().toISOString()
  const readOwnedSession = () => supabase
    .from('janet_chat_sessions')
    .select('id, title, message_count, entity_type, last_message_at')
    .eq('id', sessionId)
    .eq('user_id', userId)
    .maybeSingle()
  let { data: row, error } = await readOwnedSession()
  if (error) return // table missing or transient — the chat never depends on this
  const anchor = {
    entity_type: page?.entityType ? String(page.entityType) : null,
    entity_id: page?.entityId ? String(page.entityId) : null,
    entity_name: page?.entityName || null,
  }
  if (!row) {
    const { error: insertError } = await supabase.from('janet_chat_sessions').insert({
      id: sessionId,
      user_id: userId,
      title: role === 'user' ? deriveTitle(content) : null,
      ...anchor,
      last_message_at: now,
      message_count: increment,
    })
    if (!insertError) { notifySessionsChanged(); return }
    if (insertError.code !== '23505') return
    // A server handoff may establish the minimal topic after our read. Re-read
    // only our own row and complete its metadata; never overwrite another owner.
    ;({ data: row, error } = await readOwnedSession())
    if (error || !row) return
  }
  const patch = {
    last_message_at: row.last_message_at && row.last_message_at > now ? row.last_message_at : now,
    updated_at: now,
    message_count: (row.message_count || 0) + increment,
  }
  if (!row.title && role === 'user') patch.title = deriveTitle(content)
  if (!row.entity_type && anchor.entity_type) Object.assign(patch, anchor)
  const { error: updateError } = await supabase.from('janet_chat_sessions').update(patch)
    .eq('id', sessionId).eq('user_id', userId)
  if (updateError) return
  notifySessionsChanged()
}

/**
 * The topic Janet opened for an offer or noticing, if any (null before
 * 20261012c). There is one conversation per origin EVER — archived counts:
 * an archived topic is still the one conversation for this thing, and must
 * be found and reused rather than minting a second one.
 */
export async function findOriginTopic(userId, originType, originId) {
  if (!userId || !originId || openerColsMissing) return null
  const { data, error } = await supabase
    .from('janet_chat_sessions')
    .select('id')
    .eq('user_id', userId)
    .eq('origin_type', originType)
    .eq('origin_id', String(originId))
    .limit(1)
  if (error) return null
  return data?.[0]?.id || null
}

/**
 * The origin (type/id) a session was opened for, if any — the reverse of
 * findOriginTopic. Lets a surface restore what a topic is ABOUT from the
 * session alone (e.g. reloading /janet/chat/:sessionId with no ?offer= in
 * the URL), rather than depending on a query param that only exists on the
 * first visit from a link.
 */
export async function getSessionOrigin(userId, sessionId) {
  if (!userId || !sessionId || openerColsMissing) return null
  const { data, error } = await supabase
    .from('janet_chat_sessions')
    .select('origin_type, origin_id')
    .eq('user_id', userId)
    .eq('id', sessionId)
    .maybeSingle()
  if (error || !data?.origin_type || !data?.origin_id) return null
  return { type: data.origin_type, id: data.origin_id }
}

/**
 * Idempotent fallback topic creation for an offer/noticing that has no
 * origin topic yet (findOriginTopic returned null) — e.g. this link came
 * straight from an email digest before the worker's opener
 * (api/_lib/janet-openers.js) ever ran for it. Without an origin
 * association, the session this mints could never be found again by
 * findOriginTopic, so every later visit (or the worker's own opener, once it
 * does run) would mint yet another, unassociated topic for the SAME
 * offer/noticing.
 *
 * `originKey` MUST be computed by src/lib/janetOpenerKeys.js — the same
 * algorithm the worker's opener uses — so a genuinely concurrent write from
 * either side collides on the SAME (user_id, origin_key) unique index
 * (20261012c_janet_initiated_topics.sql) and converges onto one row rather
 * than leaving two. A 23505 here means that race already happened and
 * someone else's row won; this re-reads and reuses it rather than treating
 * the conflict as failure.
 *
 * Degrades to an ordinary, unassociated topic (still usable, just not
 * dedup-safe) before the migration lands or when origin fields are missing —
 * never blocks the conversation from starting.
 */
export async function ensureOriginSession(userId, { originType, originId, originKey, title } = {}) {
  const id = newSessionId()
  if (!userId || !originId || !originKey || openerColsMissing) return id
  const { error } = await supabase.from('janet_chat_sessions').insert({
    id,
    user_id: userId,
    title: title || 'Conversation',
    origin_type: originType,
    origin_id: String(originId),
    origin_key: originKey,
    last_message_at: new Date().toISOString(),
    message_count: 0,
  })
  if (!error) { notifySessionsChanged(); return id }
  if (error.code !== '23505') {
    console.warn('[janet-sessions] ensureOriginSession failed:', error.message)
    return id   // best-effort: the conversation still works, just unassociated this time
  }
  const existing = await findOriginTopic(userId, originType, originId)
  return existing || id
}
